import React from 'react'
import { pluralizeToString } from 'lib/string/pluralize'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getLPCRegionLOS from 'lib/offer/getLPCRegionLOS'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import { useAppSelector } from 'hooks/reduxHooks'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPriceNew'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import getOfferDurationText from 'lib/offer/getOfferDurationText'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { EmptyObject } from 'lib/object/objectUtils'
import { isFlashHotelOffer, isHotelPackage } from 'lib/offer/offerTypes'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.TourOffer | App.TourOfferSummary | App.VillaOffer | App.VillaOfferSummary;
  variant: 'default' | 'condensed';
  selectedDatesAndRoomsFilter?: App.OfferListFilters;
}

function LEOfferForceBundlePricing(props: Props) {
  const {
    offer,
    variant,
    selectedDatesAndRoomsFilter = EmptyObject,
  } = props

  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter)
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  const flightOrigin = useAppSelector(state => getDefaultAirportCode(state))
  const flightPrice = offer.flightPrices[flightOrigin || '']
  const {
    withFlights: pricingWithFlights,
  } = useOfferPackagePrice(pkg, offer, { flightPrice })

  if (!pricingWithFlights) {
    return null
  }

  const totalPriceWithFlights = pricingWithFlights.price
  const displayTotalValue = pricingWithFlights.value
  const discountPercent = pricingWithFlights.discountPercent

  const shouldShowRate = !offer.isDiscountPillHidden && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const shouldShowDiscountPercent = offer.type === OFFER_TYPE_LAST_MINUTE
  const shouldShowValue = pkg.value > 0 && totalPriceWithFlights && displayTotalValue > totalPriceWithFlights && pkg.shouldDisplayValue &&
    !shouldShowDiscountPercent && (isFlashHotelOffer(offer) || (isHotelPackage(pkg) && !pkg.roomRate?.inclusionsHideValue))

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration
  const durationText = getOfferDurationText(offer, pkg)

  return (<>
    <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
    <PriceRowPrice
      price={totalPriceWithFlights}
      saleUnit={pricingWithFlights.saleUnit}
      size="L"
    />
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      size="L"
      originalValue={Math.ceil(displayTotalValue)}
      discountPercentage={shouldShowRate ? discountPercent : undefined}
    />}
    {staysFrom && showStaysFromLabel && <PriceRowCaption>
      Stays from {pluralizeToString('night', staysFrom)} available
    </PriceRowCaption>}
    {!!pkg.roomOccupancy && offer.type === 'tour' && <PriceRowCaption>
      {pkg.roomOccupancy}
    </PriceRowCaption>}
    {variant === 'default' && <PriceRowTaxesAndFees />}
  </>)
}

export default LEOfferForceBundlePricing
